<template>
  <div class="cart-page">
    <div class="cart-header">
      <h1 class="cart-title">我的购物车</h1>
      <span class="cart-count">共 {{ totalQuantity }} 件商品</span>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>加载购物车数据中...</p>
    </div>

      <div v-else-if="cartItems.length === 0" class="empty-cart">
        <div class="empty-icon" aria-hidden="true"></div>
      <p class="empty-text">您的购物车还是空的</p>
      <router-link to="/" class="btn btn-primary empty-btn">去逛逛</router-link>
    </div>

    <div v-else class="cart-container">
      <div class="cart-main">
        <div class="cart-toolbar">
          <label class="checkbox-label select-all">
            <input type="checkbox" id="selectAll" v-model="isAllSelected" @change="toggleAllSelection">
            <span class="checkbox-custom"></span>
            <span class="label-text">全选</span>
          </label>
          <div class="toolbar-actions">
            <button class="action-btn" @click="clearCart" v-if="cartItems.length > 0">
              <i class="icon" aria-hidden="true"></i> 清空购物车
            </button>
          </div>
        </div>

        <div class="cart-items-list">
          <div 
            v-for="item in cartItems" 
            :key="item.productId" 
            class="cart-item-card"
            :class="{ 'selected': selectedItems.includes(item.productId) }"
          >
            <label class="checkbox-label item-checkbox">
              <input type="checkbox" :value="item.productId" v-model="selectedItems" @change="updateAllSelected">
              <span class="checkbox-custom"></span>
            </label>
            
            <div class="item-image-wrapper" @click="goToProduct(item.productId)">
              <img 
                :src="getProductImageUrl(item)" 
                :alt="item.productName"
                :data-product-id="item.productId"
                @error="handleImageError"
                class="item-image"
              />
            </div>
            
            <div class="item-info">
              <h3 class="item-name" @click="goToProduct(item.productId)">{{ item.productName }}</h3>
              <div class="item-specs">
                <template v-if="item.sizeName">
                  <span class="spec-tag">尺码</span>
                  <span class="spec-value">{{ item.sizeName }}</span>
                  <span v-if="item.sizeExtraPrice > 0" class="spec-extra">(+¥{{ item.sizeExtraPrice.toFixed(2) }})</span>
                </template>
                <template v-else>
                  <span class="spec-tag">颜色</span>
                  <span class="spec-value">默认</span>
                </template>
              </div>
              <div class="item-bottom">
                <div class="price-section">
                  <span class="price-symbol">¥</span>
                  <span class="price-value">{{ item.price.toFixed(2) }}</span>
                </div>
                <div class="quantity-control">
                  <button 
                    class="qty-btn minus" 
                    @click="updateQuantity(item.productId, item.quantity - 1)" 
                    :disabled="item.quantity <= 1"
                  >
                    <span>-</span>
                  </button>
                  <input 
                    type="number" 
                    class="qty-input" 
                    :value="item.quantity"
                    @change="handleQuantityInput($event, item)"
                    min="1"
                  />
                  <button 
                    class="qty-btn plus" 
                    @click="updateQuantity(item.productId, item.quantity + 1)"
                  >
                    <span>+</span>
                  </button>
                </div>
                <div class="subtotal-section">
                  <span class="subtotal-label">小计</span>
                  <span class="subtotal-price">¥{{ (item.price * item.quantity).toFixed(2) }}</span>
                </div>
              </div>
            </div>
            
            <button class="remove-btn" @click="removeFromCart(item.productId)" title="删除商品">
              <span>删除</span>
            </button>
          </div>
        </div>
      </div>

      <div class="cart-sidebar">
        <div class="sidebar-card">
          <div class="sidebar-header">
            <h3>结算</h3>
            <span class="selected-info" v-if="selectedItems.length > 0">已选 {{ selectedItems.length }} 件</span>
          </div>
          
          <div class="summary-row">
            <span class="summary-label">商品金额</span>
            <span class="summary-value">¥{{ totalAmount.toFixed(2) }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">运费</span>
            <span class="summary-value free">免运费</span>
          </div>
          <div class="summary-row total-row">
            <span class="summary-label">合计</span>
            <div class="total-price">
              <span class="price-symbol">¥</span>
              <span class="price-value">{{ totalAmount.toFixed(2) }}</span>
            </div>
          </div>
          
          <div class="promotion-tip">
            <span class="tip-icon" aria-hidden="true"></span>
            <span>满99元免运费</span>
          </div>
          
          <button 
            class="btn checkout-btn" 
            :class="{ 'disabled': selectedItems.length === 0 }"
            @click="checkout"
          >
            <span class="btn-text">去结算</span>
            <span class="btn-price" v-if="selectedItems.length > 0">¥{{ selectedTotalAmount.toFixed(2) }}</span>
          </button>
          
          <div class="batch-actions" v-if="selectedItems.length > 0">
            <button class="batch-btn favorite-btn" @click="batchFavorite">
              <i class="icon" aria-hidden="true"></i> 收藏选中
            </button>
            <button class="batch-btn delete-btn" @click="confirmBatchDelete">
              <i class="icon" aria-hidden="true"></i> 删除选中
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 结算确认弹窗 - 使用统一CheckoutForm组件 -->
    <div v-if="showCheckoutModal" class="modal-overlay" @click.self="closeCheckoutModal">
      <div class="checkout-modal unified-checkout">
        <div class="modal-header">
          <h2>确认订单</h2>
          <button class="close-btn" @click="closeCheckoutModal">×</button>
        </div>
        <CheckoutForm 
          :items="selectedCartItems" 
          @close="closeCheckoutModal"
          @order-created="onOrderCreated"
          @payment-success="onPaymentSuccess"
        />
      </div>
    </div>

    <!-- 统一非模态Toast提示 -->
    <div class="toast-container">
      <div 
        v-for="toast in toasts" 
        :key="toast.id" 
        class="toast"
        :class="toast.type"
      >
        <span class="toast-icon">{{ getToastIcon(toast.type) }}</span>
        <span class="toast-message">{{ toast.message }}</span>
        <span v-if="toast.showUndo" class="toast-undo" @click="undoRemove">撤销</span>
        <button class="toast-close" @click="removeToast(toast.id)">×</button>
      </div>
    </div>
  </div>
</template>

<script>
import CheckoutForm from '@/components/CheckoutForm.vue';

export default {
  name: 'CartView',
  components: {
    CheckoutForm
  },
  data() {
    return {
      cartItems: [],
      totalQuantity: 0,
      totalAmount: 0,
      loading: true,
      selectedItems: [],
      isAllSelected: false,
      isCustomerLoggedIn: false,
      customerUsername: '',
      
      showCheckoutModal: false,
      createdOrderId: null,
      
      // 撤销删除功能
      deletedItems: [],
      undoTimer: null,
      
      // 统一非模态Toast提示
      toasts: []
    };
  },
  computed: {
    selectedCartItems() {
      return this.cartItems.filter(item => this.selectedItems.includes(item.productId));
    },
    selectedTotalAmount() {
      return this.selectedCartItems.reduce((total, item) => {
        return total + (item.price * item.quantity);
      }, 0);
    }
  },
  created() {
    window.addEventListener('storage', this.handleStorageChange);
    this.checkLoginStatus();
    this.$nextTick(() => {
      this.fetchCartItems();
    });
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.handleStorageChange);
  },
  methods: {
    checkLoginStatus() {
      this.isCustomerLoggedIn = !!localStorage.getItem('customerToken');
      this.customerUsername = localStorage.getItem('customerUsername') || '';
    },
    
    handleStorageChange(event) {
      if (event.key === 'customerToken' || event.key === 'customerUsername') {
        this.checkLoginStatus();
        this.fetchCartItems();
      }
    },
    
    getProductImageUrl(item) {
      if (!item.imageUrl) {
        return `https://picsum.photos/seed/product-${item.productId}/180/180.jpg`;
      }
      if (!item.imageUrl.startsWith('http')) {
        return item.imageUrl;
      }
      if (item.imageUrl.includes('huaban.com')) {
        return `https://picsum.photos/seed/product-${item.productId}/180/180.jpg`;
      }
      return item.imageUrl;
    },
    
    handleImageError(event) {
      const productId = event.target.dataset.productId || 'default';
      const defaultImage = `https://picsum.photos/seed/product-${productId}/180/180.jpg`;
      if (event.target.src !== defaultImage) {
        event.target.src = defaultImage;
      }
    },
    
    goToProduct(productId) {
      this.$router.push(`/product/${productId}`);
    },
    
    async fetchCartItems() {
      if (!this.isCustomerLoggedIn) {
        this.loading = false;
        return;
      }
      
      this.loading = true;
      try {
        const username = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(username || '');
        const response = await this.$axios.get('/cart/items', {
          headers: { 'X-Username': encodedUsername }
        });
        if (response.data.success) {
          this.cartItems = response.data.cartItems;
          this.totalQuantity = response.data.totalQuantity;
          this.calculateTotalAmount();
        } else {
          this.showError('获取购物车数据失败: ' + response.data.message);
        }
      } catch (error) {
        console.error('获取购物车数据失败:', error);
        this.showError('获取购物车数据失败，请稍后重试。');
      } finally {
        this.loading = false;
      }
    },
    
    calculateTotalAmount() {
      this.totalAmount = this.cartItems.reduce((total, item) => {
        return total + (item.price * item.quantity);
      }, 0);
    },
    
    async updateQuantity(productId, quantity) {
      try {
        const item = this.cartItems.find(item => item.productId === productId);
        if (!item) return;
        
        if (quantity <= 0) {
          quantity = 1;
        }
        
        const username = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(username || '');
        const response = await this.$axios.put('/cart/update', {
          productId: productId,
          quantity: quantity
        }, { headers: { 'X-Username': encodedUsername } });
        
        if (response.data.success) {
          this.cartItems = response.data.cartItems;
          this.totalQuantity = response.data.totalQuantity;
          this.calculateTotalAmount();
        } else {
          this.showError('更新数量失败: ' + response.data.message);
        }
      } catch (error) {
        console.error('更新数量失败:', error);
        this.showError('更新数量失败: ' + (error.response?.data?.message || '请稍后重试'));
      }
    },
    
    handleQuantityInput(event, item) {
      const value = parseInt(event.target.value);
      if (!isNaN(value) && value > 0) {
        this.updateQuantity(item.productId, value);
      } else {
        event.target.value = item.quantity;
      }
    },
    
    async removeFromCart(productId) {
      try {
        // 保存要删除的商品信息
        const itemToRemove = this.cartItems.find(item => item.productId === productId);
        if (!itemToRemove) return;
        
        const username = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(username || '');
        const response = await this.$axios.delete(`/cart/remove/${productId}`, {
          headers: { 'X-Username': encodedUsername }
        });
        
        if (response.data.success) {
          this.cartItems = response.data.cartItems;
          this.totalQuantity = response.data.totalQuantity;
          this.calculateTotalAmount();
          this.selectedItems = this.selectedItems.filter(id => id !== productId);
          this.updateAllSelected();
          
          // 保存删除的商品用于撤销
          this.deletedItems.push(itemToRemove);
          
          // 清除之前的定时器
          if (this.undoTimer) {
            clearTimeout(this.undoTimer);
          }
          
          // 显示撤销Toast提示
          const toast = {
            id: Date.now() + Math.random(),
            message: '已删除商品',
            type: 'info',
            showUndo: true
          };
          this.toasts.push(toast);
          
          // 3秒后自动消失并清除删除的商品
          this.undoTimer = setTimeout(() => {
            const index = this.toasts.findIndex(t => t.id === toast.id);
            if (index !== -1) {
              this.toasts.splice(index, 1);
            }
            this.deletedItems = [];
          }, 3000);
        } else {
          this.showError('移除商品失败: ' + response.data.message);
        }
      } catch (error) {
        console.error('移除商品失败:', error);
        this.showError('移除商品失败，请稍后重试');
      }
    },
    
    async undoRemove() {
      if (this.deletedItems.length === 0) return;
      
      try {
        const username = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(username || '');
        
        // 重新添加所有删除的商品
        for (const item of this.deletedItems) {
          await this.$axios.post('/cart/add', {
            productId: item.productId,
            quantity: item.quantity,
            sizeDetailId: item.sizeDetailId || null
          }, {
            headers: { 'X-Username': encodedUsername }
          });
        }
        
        // 重新获取购物车数据
        await this.fetchCartItems();
        
        // 清除撤销状态和相关Toast
        this.deletedItems = [];
        if (this.undoTimer) {
          clearTimeout(this.undoTimer);
          this.undoTimer = null;
        }
        
        // 移除showUndo类型的Toast
        const undoToastIndex = this.toasts.findIndex(t => t.showUndo);
        if (undoToastIndex !== -1) {
          this.toasts.splice(undoToastIndex, 1);
        }
        
        this.showSuccess('已恢复删除的商品');
      } catch (error) {
        console.error('撤销删除失败:', error);
        this.showError('撤销删除失败，请稍后重试');
      }
    },
    
    confirmBatchDelete() {
      if (this.selectedItems.length === 0) {
        this.showError('请先选择要删除的商品');
        return;
      }
      
      if (confirm(`确定要删除选中的 ${this.selectedItems.length} 件商品吗？`)) {
        this.batchDelete();
      }
    },
    
    async batchDelete() {
      if (!this.isCustomerLoggedIn) {
        this.showError('请先登录');
        return;
      }
      
      try {
        const username = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(username || '');
        
        let successCount = 0;
        
        for (const productId of this.selectedItems) {
          try {
            const response = await this.$axios.delete(`/cart/remove/${productId}`, {
              headers: { 'X-Username': encodedUsername }
            });
            if (response.data.success) {
              successCount++;
            }
          } catch (error) {
            console.error(`删除商品 ${productId} 失败:`, error);
          }
        }
        
        await this.fetchCartItems();
        this.selectedItems = [];
        this.isAllSelected = false;
        
        if (successCount === this.selectedItems.length) {
          this.showError(`成功删除 ${successCount} 件商品`);
        }
      } catch (error) {
        console.error('批量删除失败:', error);
        this.showError('批量删除失败，请稍后重试');
      }
    },
    
    async clearCart() {
      if (!this.isCustomerLoggedIn) {
        this.showError('请先登录');
        return;
      }
      
      if (confirm('确定要清空购物车吗？')) {
        try {
          const username = localStorage.getItem('customerUsername');
          const encodedUsername = encodeURIComponent(username || '');
          
          for (const item of this.cartItems) {
            try {
              await this.$axios.delete(`/cart/remove/${item.productId}`, {
                headers: { 'X-Username': encodedUsername }
              });
            } catch (error) {
              console.error(`删除商品 ${item.productId} 失败:`, error);
            }
          }
          
          await this.fetchCartItems();
          this.selectedItems = [];
          this.isAllSelected = false;
        } catch (error) {
          console.error('清空购物车失败:', error);
          this.showError('清空购物车失败，请稍后重试。');
        }
      }
    },
    
    toggleAllSelection() {
      if (this.isAllSelected) {
        this.selectedItems = this.cartItems.map(item => item.productId);
      } else {
        this.selectedItems = [];
      }
    },
    
    updateAllSelected() {
      this.isAllSelected = this.selectedItems.length === this.cartItems.length;
    },
    
    showToast(message, type = 'info', duration = 3000) {
      const toast = {
        id: Date.now() + Math.random(),
        message,
        type
      };
      this.toasts.push(toast);
      
      if (duration > 0) {
        setTimeout(() => {
          this.removeToast(toast.id);
        }, duration);
      }
    },
    
    showError(msg) {
      this.showToast(msg, 'error');
    },
    
    showSuccess(msg) {
      this.showToast(msg, 'success');
    },
    
    showInfo(msg) {
      this.showToast(msg, 'info');
    },
    
    showWarning(msg) {
      this.showToast(msg, 'warning');
    },
    
    removeToast(id) {
      const index = this.toasts.findIndex(toast => toast.id === id);
      if (index !== -1) {
        this.toasts.splice(index, 1);
      }
    },
    
    getToastIcon(type) {
      const icons = {
        success: '✓',
        error: '✕',
        info: 'ℹ',
        warning: '!'
      };
      return icons[type] || 'ℹ';
    },
    
    async checkout() {
      if (this.selectedItems.length === 0) {
        this.showError('请先选择要购买的商品');
        return;
      }
      
      if (!this.isCustomerLoggedIn) {
        this.showError('请先登录');
        setTimeout(() => {
          this.$router.push('/login');
        }, 1500);
        return;
      }
      
      await this.fetchAddresses();
      this.showCheckoutModal = true;
    },
    
    async fetchAddresses() {
      this.loadingAddresses = true;
      try {
        const username = localStorage.getItem('customerUsername');
        const response = await this.$axios.get('/addresses', {
          headers: { 'X-Username': username }
        });
        
        if (response.data.success) {
          this.addresses = response.data.addresses || [];
          if (this.addresses.length > 0) {
            if (this.selectedAddress) {
              const updatedAddress = this.addresses.find(a => a.id === this.selectedAddress.id);
              if (updatedAddress) {
                this.selectedAddress = updatedAddress;
                this.tempSelectedAddressId = updatedAddress.id;
              } else {
                const defaultAddr = this.addresses.find(a => a.isDefault);
                this.selectedAddress = defaultAddr || this.addresses[0];
                this.tempSelectedAddressId = this.selectedAddress.id;
              }
            } else {
              const defaultAddr = this.addresses.find(a => a.isDefault);
              this.selectedAddress = defaultAddr || this.addresses[0];
              this.tempSelectedAddressId = this.selectedAddress.id;
            }
          }
        }
      } catch (error) {
        console.error('获取地址失败:', error);
      } finally {
        this.loadingAddresses = false;
      }
    },
    
    confirmAddressSelection() {
      if (this.tempSelectedAddressId) {
        this.selectedAddress = this.addresses.find(a => a.id === this.tempSelectedAddressId);
        this.showAddressSelector = false;
      }
    },
    
    closeCheckoutModal() {
      this.showCheckoutModal = false;
    },
    
    onOrderCreated(orderId) {
      this.createdOrderId = orderId;
      this.showSuccess('订单创建成功');
    },
    
    onPaymentSuccess() {
      this.showSuccess('支付成功');
      this.fetchCartItems();
      this.selectedItems = [];
      this.isAllSelected = false;
    },
    
    async createOrder() {
      if (this.submitting) return;
      if (!this.selectedAddress) {
        this.showError('请选择收货地址');
        return;
      }
      
      try {
        this.submitting = true;
        const username = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(username || '');
        
        // 创建订单前缓存选中商品，用于返回上一步时恢复显示
        this.checkoutItems = JSON.parse(JSON.stringify(this.selectedCartItems));
        
        const orderItems = this.selectedCartItems.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.price,
          productName: item.productName,
          sizeDetailId: item.sizeDetailId || null,
          sizeName: item.sizeName || null,
          sizeExtraPrice: item.sizeExtraPrice || 0
        }));
        
        const orderRequest = {
          items: orderItems,
          buyerName: this.selectedAddress.receiverName,
          phone: this.selectedAddress.phone,
          address: this.selectedAddress.province + this.selectedAddress.city + this.selectedAddress.district + this.selectedAddress.detailAddress,
          notes: this.orderNotes
        };
        
        const response = await this.$axios.post('/customer-orders/create', orderRequest, {
          headers: { 'X-Username': encodedUsername }
        });
        
        if (response.data.success) {
          this.createdOrderId = response.data.orderId;
          this.createdOrderAmount = response.data.totalAmount || this.selectedTotalAmount;
          this.showCheckoutModal = false;
          this.orderNotes = '';
          this.currentStep = 2;
          this.showPaymentModal = true;
          
          await this.fetchCartItems();
          this.selectedItems = [];
          this.isAllSelected = false;
        } else {
          this.showError('创建订单失败: ' + response.data.message);
        }
      } catch (error) {
        console.error('创建订单失败:', error);
        this.showError('创建订单失败: ' + (error.response?.data?.message || '请稍后重试'));
      } finally {
        this.submitting = false;
      }
    },
    
    closePaymentModal() {
      this.showPaymentModal = false;
      this.currentStep = 1;
    },
    
    async payOrder() {
      if (this.paying) return;
      
      try {
        this.paying = true;
        const username = localStorage.getItem('customerUsername');
        const encodedUsername = encodeURIComponent(username || '');
        
        await this.$axios.post(`/customer-orders/${this.createdOrderId}/pay`, {}, {
          headers: { 'X-Username': encodedUsername }
        });
        
        this.currentStep = 3;
        this.showPaymentModal = false;
        this.showPaymentSuccess = true;
      } catch (error) {
        console.error('支付失败:', error);
        this.showError('支付失败: ' + (error.response?.data?.message || '请稍后重试'));
      } finally {
        this.paying = false;
      }
    },
    
    goToStep(step) {
      if (step < this.currentStep) {
        this.currentStep = step;
        if (step === 1) {
          this.showPaymentModal = false;
          this.showPaymentSuccess = false;
          this.showCheckoutModal = true;
          
          // 从支付界面返回上一步时，恢复缓存的商品信息
          if (this.checkoutItems.length > 0) {
            // 将缓存的商品添加回购物车显示
            for (const item of this.checkoutItems) {
              const existingItem = this.cartItems.find(i => i.productId === item.productId);
              if (!existingItem) {
                this.cartItems.push(item);
              }
            }
            // 恢复选中状态
            this.selectedItems = this.checkoutItems.map(item => item.productId);
            this.isAllSelected = false;
            this.calculateTotalAmount();
          }
        } else if (step === 2) {
          this.showPaymentSuccess = false;
          this.showPaymentModal = true;
        }
      }
    },
    
    viewOrderDetail() {
      this.showPaymentSuccess = false;
      // 支付成功后清空缓存
      this.checkoutItems = [];
      this.$router.push('/orders');
    },
    
    continueShopping() {
      this.showPaymentSuccess = false;
      this.createdOrderId = null;
      // 支付成功后清空缓存
      this.checkoutItems = [];
      this.$router.push('/');
    },
    
    async batchFavorite() {
      if (!localStorage.getItem('customerToken')) {
        this.showError('请先登录再进行收藏操作');
        setTimeout(() => {
          this.$router.push('/login');
        }, 1500);
        return;
      }
      
      try {
        const username = localStorage.getItem('customerUsername');
        let successCount = 0;
        
        for (const productId of this.selectedItems) {
          try {
            const checkResponse = await this.$axios.get(`/favorites/${username}/${productId}`);
            if (!checkResponse.data.isFavorited) {
              await this.$axios.post(`/favorites/${username}/${productId}`);
              successCount++;
            }
          } catch (err) {
            console.error(`收藏商品 ${productId} 失败:`, err);
          }
        }
        
        if (successCount > 0) {
          this.showError(`已成功收藏 ${successCount} 件商品！`);
        } else {
          this.showError('所有选中的商品都已在收藏夹中');
        }
      } catch (error) {
        console.error('批量收藏失败:', error);
        this.showError('批量收藏失败，请稍后重试。');
      }
    }
  }
};
</script>

<style scoped>
.cart-page {
  min-height: calc(100vh - 80px);
  background-color: #f5f5f5;
  padding: 20px 0;
}

.cart-header {
  max-width: 1200px;
  margin: 0 auto 20px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.cart-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.cart-count {
  font-size: 14px;
  color: #999;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.loading-container.small {
  padding: 30px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #ff4400;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-cart {
  max-width: 400px;
  margin: 40px auto;
  text-align: center;
  padding: 60px 20px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.empty-icon {
  position: relative;
  width: 72px;
  height: 72px;
  font-size: 0;
  margin-bottom: 20px;
  margin-inline: auto;
  border-radius: 24px;
  background: linear-gradient(145deg, #ffffff, #e9edf5);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
}

.empty-icon::before,
.empty-icon::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.empty-icon::before {
  top: 18px;
  width: 34px;
  height: 28px;
  border: 2px solid #111827;
  border-radius: 8px 8px 12px 12px;
}

.empty-icon::after {
  top: 12px;
  width: 18px;
  height: 10px;
  border: 2px solid #111827;
  border-bottom: 0;
  border-radius: 12px 12px 0 0;
}

.empty-text {
  font-size: 16px;
  color: #999;
  margin-bottom: 25px;
}

.empty-btn {
  padding: 12px 40px;
  font-size: 16px;
  border-radius: 25px;
}

.cart-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  gap: 20px;
}

.cart-main {
  flex: 1;
  min-width: 0;
}

.cart-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: white;
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.checkbox-label input {
  display: none;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-radius: 4px;
  position: relative;
  margin-right: 8px;
  transition: all 0.2s ease;
}

.checkbox-label input:checked + .checkbox-custom {
  background-color: #ff4400;
  border-color: #ff4400;
}

.checkbox-label input:checked + .checkbox-custom::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.select-all .label-text {
  font-size: 14px;
  color: #333;
}

.toolbar-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 8px 16px;
  font-size: 14px;
  color: #666;
  background-color: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;
}

.action-btn .icon,
.batch-btn .icon {
  position: relative;
  width: 18px;
  height: 18px;
  font-size: 0;
  flex: 0 0 18px;
  display: inline-flex;
}

.action-btn .icon::before,
.action-btn .icon::after,
.batch-btn .icon::before,
.batch-btn .icon::after {
  content: '';
  position: absolute;
}

.action-btn .icon::before,
.delete-btn .icon::before {
  inset: 5px 3px 2px;
  border: 2px solid currentColor;
  border-top: 0;
  border-radius: 0 0 4px 4px;
}

.action-btn .icon::after,
.delete-btn .icon::after {
  top: 2px;
  left: 4px;
  width: 10px;
  height: 2px;
  background: currentColor;
  box-shadow: 2px -3px 0 -1px currentColor;
}

.action-btn:hover {
  background-color: #f5f5f5;
  color: #ff4400;
}

.cart-items-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.cart-item-card {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
}

.cart-item-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.cart-item-card.selected {
  border: 2px solid #ff4400;
  background-color: #fff8f5;
}

.item-checkbox {
  margin-right: 15px;
}

.item-image-wrapper {
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  margin-right: 20px;
  cursor: pointer;
}

.item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.item-name {
  font-size: 16px;
  color: #333;
  margin: 0 0 10px 0;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-name:hover {
  color: #ff4400;
}

.item-specs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
}

.spec-tag {
  font-size: 12px;
  color: #999;
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
}

.spec-value {
  font-size: 13px;
  color: #666;
}

.spec-extra {
  font-size: 12px;
  color: #E02E24;
  font-weight: 500;
}

.item-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
}

.price-section {
  display: flex;
  align-items: baseline;
}

.price-symbol {
  font-size: 14px;
  color: #ff4400;
  font-weight: bold;
}

.price-value {
  font-size: 24px;
  color: #ff4400;
  font-weight: bold;
}

.quantity-control {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 20px;
  padding: 2px;
}

.qty-btn {
  width: 32px;
  height: 32px;
  border: none;
  background-color: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: #666;
  transition: all 0.2s ease;
}

.qty-btn:hover:not(:disabled) {
  background-color: #ff4400;
  color: white;
}

.qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.qty-input {
  width: 50px;
  height: 32px;
  border: none;
  background-color: transparent;
  text-align: center;
  font-size: 14px;
  outline: none;
}

.subtotal-section {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.subtotal-label {
  font-size: 12px;
  color: #999;
}

.subtotal-price {
  font-size: 18px;
  color: #ff4400;
  font-weight: bold;
}

.remove-btn {
  margin-left: 20px;
  padding: 8px 16px;
  font-size: 13px;
  color: #999;
  background-color: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.remove-btn:hover {
  color: #ff4400;
  background-color: #fff0eb;
}

.cart-sidebar {
  width: 320px;
  flex-shrink: 0;
}

.sidebar-card {
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 20px;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
}

.sidebar-header h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.selected-info {
  font-size: 13px;
  color: #ff4400;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.summary-label {
  font-size: 14px;
  color: #666;
}

.summary-value {
  font-size: 14px;
  color: #333;
}

.summary-value.free {
  color: #26a69a;
}

.total-row {
  padding-top: 15px;
  margin-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.total-price {
  display: flex;
  align-items: baseline;
  color: #ff4400;
}

.total-price .price-symbol {
  font-size: 16px;
}

.total-price .price-value {
  font-size: 28px;
}

.promotion-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: #fff7f5;
  border-radius: 8px;
  margin: 15px 0;
  font-size: 13px;
  color: #ff4400;
}

.tip-icon {
  position: relative;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  font-size: 0;
}

.tip-icon::before,
.tip-icon::after {
  content: '';
  position: absolute;
}

.tip-icon::before {
  inset: 4px;
  border: 2px solid #111827;
  border-radius: 5px;
}

.tip-icon::after {
  top: 3px;
  left: 9px;
  width: 2px;
  height: 14px;
  background: #111827;
}

.checkout-btn {
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #ff4400 0%, #ff6a00 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  transition: all 0.2s ease;
}

.checkout-btn:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 68, 0, 0.3);
}

.checkout-btn.disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-text {
  flex: 1;
}

.btn-price {
  font-size: 18px;
}

.batch-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

.batch-btn {
  flex: 1;
  padding: 10px;
  font-size: 13px;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition: all 0.2s ease;
}

.favorite-btn {
  background-color: #fff8f0;
  color: #ff9800;
  border-color: #ffd5b8;
}

.favorite-btn:hover {
  background-color: #fff0e0;
}

.favorite-btn .icon::before,
.favorite-btn .icon::after {
  top: 4px;
  width: 8px;
  height: 12px;
  background: currentColor;
  border-radius: 8px 8px 0 0;
}

.favorite-btn .icon::before {
  left: 3px;
  transform: rotate(-45deg);
  transform-origin: bottom right;
}

.favorite-btn .icon::after {
  right: 3px;
  transform: rotate(45deg);
  transform-origin: bottom left;
}

.delete-btn {
  background-color: #fff0f0;
  color: #f44336;
  border-color: #ffcdd2;
}

.delete-btn:hover {
  background-color: #ffebee;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}

.modal-overlay > div {
  background-color: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  animation: modalFadeIn 0.3s ease;
  display: flex;
  flex-direction: column;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h2,
.modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background-color: #f5f5f5;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background-color: #e0e0e0;
  color: #333;
}

.modal-body {
  padding: 24px;
  flex: 1;
  overflow-y: auto;
  max-height: calc(90vh - 180px);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  position: sticky;
  bottom: 0;
  background-color: white;
  z-index: 10;
}

.btn {
  padding: 10px 24px;
  font-size: 14px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background-color: #ff4400;
  color: white;
}

.btn-primary:hover {
  background-color: #e63900;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #666;
}

.btn-secondary:hover {
  background-color: #e8e8e8;
}

.btn-outline {
  background-color: transparent;
  border: 1px solid #ddd;
  color: #666;
}

.btn-outline:hover {
  border-color: #ff4400;
  color: #ff4400;
}

.btn-sm {
  padding: 6px 16px;
  font-size: 12px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.title-icon {
  font-size: 18px;
}

.section-title h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.address-section {
  margin-bottom: 24px;
}

.address-card {
  padding: 16px;
  background-color: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
}

.address-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.receiver-name {
  font-size: 16px;
  font-weight: 600;
}

.phone {
  font-size: 14px;
  color: #666;
}

.product-size {
  font-size: 13px;
  color: #666;
  margin: 4px 0;
}

.product-size span {
  color: #ff4400;
}

.address-modal .address-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.address-modal .receiver-name {
  font-size: 16px;
  font-weight: 600;
  min-width: 80px;
}

.address-modal .phone {
  font-size: 14px;
  color: #666;
  min-width: 110px;
}

.address-modal .default-tag {
  font-size: 12px;
  color: white;
  background-color: #ff4400;
  padding: 2px 8px;
  border-radius: 4px;
}

.address-detail {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.no-address {
  text-align: center;
  padding: 24px;
  background-color: #fafafa;
  border-radius: 8px;
}

.no-address p {
  margin-bottom: 16px;
  color: #999;
}

.products-section {
  margin-bottom: 24px;
}

.order-products {
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
}

.order-product {
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #f5f5f5;
}

.order-product:last-child {
  border-bottom: none;
}

.product-img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  margin-right: 12px;
}

.product-info {
  flex: 1;
  min-width: 0;
}

.product-info .product-name {
  font-size: 14px;
  color: #333;
  margin: 0 0 4px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-info .product-price {
  font-size: 14px;
  color: #ff4400;
  margin: 0;
}

.product-qty {
  font-size: 13px;
  color: #999;
  margin-right: 16px;
}

.product-total {
  font-size: 14px;
  font-weight: 600;
  color: #ff4400;
}

.amount-section {
  padding: 16px;
  background-color: #fafafa;
  border-radius: 8px;
  margin-bottom: 24px;
}

.amount-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
}

.amount-row.total {
  padding-top: 12px;
  margin-top: 8px;
  border-top: 1px dashed #ddd;
  font-weight: 600;
  font-size: 16px;
  color: #ff4400;
}

.notes-section {
  margin-bottom: 16px;
}

.notes-section label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
}

.notes-section textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;
  outline: none;
}

.notes-section textarea:focus {
  border-color: #ff4400;
}

.address-modal {
  max-width: 500px;
}

.address-list {
  padding: 0 24px;
  max-height: 400px;
  overflow-y: auto;
}

.address-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 2px solid transparent;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.address-option:hover {
  border-color: #ff4400;
  background-color: #fff8f5;
}

.address-option.selected {
  border-color: #ff4400;
  background-color: #fff8f5;
}

.address-radio input {
  display: none;
}

.address-radio label {
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  margin-top: 2px;
}

.address-option.selected .address-radio label {
  border-color: #ff4400;
}

.address-option.selected .address-radio label::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  background-color: #ff4400;
  border-radius: 50%;
}

.address-content {
  flex: 1;
}

.payment-modal {
  max-width: 450px;
}

.payment-info {
  text-align: center;
  padding: 24px;
}

.payment-info p {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #666;
}

.total-amount {
  font-size: 28px;
  font-weight: bold;
  color: #ff4400;
  margin-top: 16px !important;
}

.payment-methods {
  padding: 0 24px;
}

.method-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 2px solid #f0f0f0;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.method-item:hover {
  border-color: #ff4400;
}

.method-item input {
  width: 20px;
  height: 20px;
}

.method-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.success-modal {
  max-width: 400px;
  text-align: center;
  padding: 40px 24px;
}

.success-icon {
  width: 80px;
  height: 80px;
  background-color: #28a745;
  color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 40px;
  font-weight: bold;
  margin: 0 auto 20px;
  animation: successBounce 0.5s ease;
}

@keyframes successBounce {
  0% { transform: scale(0); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.success-modal h3 {
  font-size: 24px;
  font-weight: 600;
  color: #28a745;
  margin: 0 0 10px 0;
}

.success-modal p {
  font-size: 14px;
  color: #666;
  margin: 0 0 24px 0;
}

.success-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

/* 统一非模态Toast提示容器 */
.toast-container {
  position: fixed;
  top: 100px;
  right: 20px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Toast基础样式 */
.toast {
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 200px;
  max-width: 350px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: toastSlideIn 0.3s ease;
  transition: all 0.2s ease;
}

.toast:hover {
  transform: translateX(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

@keyframes toastSlideIn {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Toast图标 */
.toast-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}

/* Toast消息 */
.toast-message {
  flex: 1;
  word-break: break-all;
}

/* Toast撤销按钮 */
.toast-undo {
  padding: 4px 12px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.toast-undo:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

/* Toast关闭按钮 */
.toast-close {
  width: 20px;
  height: 20px;
  border: none;
  background-color: transparent;
  font-size: 16px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.toast-close:hover {
  opacity: 1;
}

/* Toast类型样式 */
.toast.success {
  background-color: #dcfce7;
  color: #16a34a;
}

.toast.success .toast-icon {
  background-color: #22c55e;
  color: white;
}

.toast.error {
  background-color: #fee2e2;
  color: #dc2626;
}

.toast.error .toast-icon {
  background-color: #ef4444;
  color: white;
}

.toast.warning {
  background-color: #fef3c7;
  color: #d97706;
}

.toast.warning .toast-icon {
  background-color: #f59e0b;
  color: white;
}

.toast.info {
  background-color: #dbeafe;
  color: #2563eb;
}

.toast.info .toast-icon {
  background-color: #3b82f6;
  color: white;
}

@media (max-width: 768px) {
  .cart-container {
    flex-direction: column;
  }
  
  .cart-sidebar {
    width: 100%;
  }
  
  .cart-toolbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .cart-item-card {
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .item-image-wrapper {
    width: 100px;
    height: 100px;
    margin-right: 0;
  }
  
  .item-info {
    width: calc(100% - 120px);
  }
  
  .item-bottom {
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .remove-btn {
    margin-left: 0;
    margin-top: 10px;
    width: 100%;
  }
  
  .item-checkbox {
    position: absolute;
    top: 15px;
    left: 15px;
  }
  
  .cart-item-card {
    position: relative;
    padding-left: 45px;
  }
}

/* 步骤进度条样式 */
.step-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  gap: 20px;
  background-color: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #ddd;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.step.active .step-circle,
.step.completed .step-circle {
  background-color: #ff4400;
  color: white;
}

.step.completed .step-circle {
  background-color: #28a745;
}

.step-label {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
  transition: all 0.3s ease;
}

.step.active .step-label,
.step.completed .step-label {
  color: #333;
  font-weight: 500;
}

.step-line {
  width: 60px;
  height: 3px;
  background-color: #ddd;
  transition: all 0.3s ease;
}

.step-line.active {
  background-color: #ff4400;
}

.step.clickable {
  cursor: pointer;
}

.step.clickable:hover .step-circle {
  transform: scale(1.1);
  box-shadow: 0 0 10px rgba(40, 167, 69, 0.5);
}

.step.clickable:hover .step-label {
  text-decoration: underline;
}

/* 统一结算表单样式 */
.unified-checkout {
  background-color: white;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.unified-checkout .modal-header {
  flex-shrink: 0;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
}

.unified-checkout .modal-header h2 {
  margin: 0;
  font-size: 20px;
}

.unified-checkout .checkout-form {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.cart-page {
  color: var(--color-text);
}

.cart-header,
.cart-main,
.sidebar-card,
.empty-cart,
.checkout-modal,
.toast {
  border: 1px solid rgba(255, 255, 255, 0.72);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: var(--shadow-soft);
}

.cart-header {
  padding: 1.6rem 1.8rem;
  border-radius: 32px;
}

.cart-title {
  color: var(--color-text);
  letter-spacing: -0.04em;
}

.cart-count,
.summary-label,
.selected-info,
.empty-text {
  color: var(--color-text-soft);
}

.cart-main,
.sidebar-card,
.empty-cart {
  border-radius: 30px;
}

.cart-main {
  border: 0;
  background: transparent;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

.cart-items-list {
  display: grid;
  gap: 1rem;
}

.cart-item-card {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.05);
  padding: 1.2rem;
}

.cart-item-card.selected {
  border-color: rgba(0, 113, 227, 0.18);
  box-shadow: 0 0 0 1px rgba(0, 113, 227, 0.1), 0 16px 32px rgba(0, 113, 227, 0.1);
}

.item-image-wrapper,
.sidebar-card,
.checkout-modal {
  overflow: hidden;
}

.item-image-wrapper {
  border-radius: 18px;
}

.item-info {
  display: grid;
  gap: 0.85rem;
}

.item-bottom {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 1rem;
  align-items: center;
}

.item-name,
.subtotal-price,
.total-price .price-value {
  color: var(--color-text);
}

.spec-tag,
.promotion-tip,
.summary-row.free,
.subtotal-label {
  color: var(--color-text-soft);
}

.spec-tag,
.promotion-tip {
  background: rgba(15, 23, 42, 0.04);
  border-radius: 999px;
}

.qty-btn,
.remove-btn,
.batch-btn,
.action-btn {
  border-radius: 999px;
}

.qty-btn,
.remove-btn,
.batch-btn {
  border: 1px solid var(--color-border);
  background: rgba(15, 23, 42, 0.04);
  color: var(--color-text);
}

.checkout-btn {
  min-height: 54px;
  background: linear-gradient(135deg, #111113, #2c2c2e);
}

.cart-page {
  background:
    radial-gradient(circle at top left, rgba(191, 219, 254, 0.45), transparent 32%),
    linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
}

.cart-item-card,
.sidebar-card,
.cart-toolbar,
.empty-cart,
.toast {
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.cart-item-card,
.products-grid .product-card,
.sidebar-card,
.cart-toolbar {
  position: relative;
  overflow: hidden;
}

.cart-item-card {
  animation: cartCardRise 0.55s ease both;
}

.cart-item-card:nth-child(2) {
  animation-delay: 0.05s;
}

.cart-item-card:nth-child(3) {
  animation-delay: 0.1s;
}

.cart-item-card:nth-child(4) {
  animation-delay: 0.15s;
}

.item-image {
  transition: transform 0.35s ease, filter 0.35s ease;
}

.cart-item-card:hover .item-image {
  transform: scale(1.05);
  filter: saturate(1.06);
}

.cart-item-card:hover,
.sidebar-card:hover {
  transform: scale(1.012);
  border-color: rgba(0, 113, 227, 0.18);
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.1);
}

.checkout-btn,
.batch-btn,
.remove-btn,
.action-btn,
.qty-btn {
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    background-color 0.22s ease,
    border-color 0.22s ease;
}

.checkout-btn:hover:not(.disabled),
.batch-btn:hover,
.remove-btn:hover,
.action-btn:hover,
.qty-btn:hover:not(:disabled) {
  transform: translateY(-2px);
}

@keyframes cartCardRise {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.toast {
  border-radius: 18px;
}

@media (max-width: 900px) {
  .item-bottom {
    grid-template-columns: 1fr;
  }
}

</style>
